import { useMemo, useState } from 'react'
import { Box, Chip, LinearProgress, MenuItem, Stack, TextField, Typography } from '@mui/material'
import { OpenInNew } from '@mui/icons-material'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { DataTable, type DataTableColumn } from '@components/common/DataTable'
import { SearchField } from '@components/common/SearchField'
import { storageReportApi } from '@api/endpoints/storageReport'
import type { ReportSort, ReportView, ReportedMediaFile } from '@api/types/storageReport'
import { STORAGE_REPORT_KEY, apiErrorMessage, formatBytes, formatDateTime } from './storageFormat'

const SORTS: { value: ReportSort; label: string }[] = [
  { value: 'size', label: 'Largest first' },
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
]

export interface ReportFilesTableProps {
  view: ReportView
  /** Changes when a new report completes, so the list reloads instead of showing the old one. */
  reportKey: string
  emptyMessage: string
}

/** A server-paged list of media files from the last report. */
export function ReportFilesTable({ view, reportKey, emptyMessage }: ReportFilesTableProps) {
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(50)
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<ReportSort>('size')

  const query = useQuery({
    queryKey: [...STORAGE_REPORT_KEY, 'files', reportKey, view, search, sort, page, pageSize],
    queryFn: () =>
      storageReportApi.files({ view, search: search || undefined, sort, page: page + 1, pageSize }),
    placeholderData: keepPreviousData,
  })

  const columns = useMemo<DataTableColumn<ReportedMediaFile>[]>(
    () => [
      {
        key: 'preview',
        header: '',
        width: 56,
        hideBelow: 'sm',
        render: (file) => <FileThumb file={file} size={40} />,
      },
      {
        key: 'file',
        header: 'File',
        primary: true,
        render: (file) => (
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
              {file.displayName || file.fileName}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}>
              {file.filePath}
            </Typography>
          </Box>
        ),
      },
      {
        key: 'size',
        header: 'Size',
        align: 'right',
        render: (file) => <Typography variant="body2">{formatBytes(file.fileSize)}</Typography>,
      },
      {
        key: 'category',
        header: 'Category',
        hideBelow: 'md',
        render: (file) => (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            <Chip size="small" variant="outlined" label={file.category} />
            {file.inFolder && <Chip size="small" variant="outlined" label="In a folder" />}
            {view === 'missing' && file.isReferenced && <Chip size="small" color="error" label="In use" />}
          </Stack>
        ),
      },
      {
        key: 'uploaded',
        header: 'Uploaded',
        hideBelow: 'md',
        render: (file) => (
          <Box>
            <Typography variant="body2">{formatDateTime(file.createdDate)}</Typography>
            <Typography variant="caption" color="text.secondary">
              by {file.createdByDisplayName || file.createdByUserId || 'unknown'}
            </Typography>
          </Box>
        ),
      },
      {
        key: 'bucket',
        header: 'In bucket',
        hideBelow: 'lg',
        render: (file) =>
          file.inBucket == null ? (
            <Chip size="small" variant="outlined" label="Not checked" />
          ) : file.inBucket ? (
            <Chip size="small" variant="outlined" label="Yes" />
          ) : (
            <Chip size="small" color="error" label="Missing" />
          ),
      },
    ],
    [view],
  )

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.5 }}>
        <SearchField
          value={search}
          onSearch={(q) => {
            setSearch(q)
            setPage(0)
          }}
          placeholder="Search name or path"
          maxWidth="100%"
        />
        <TextField
          select
          size="small"
          label="Sort"
          value={sort}
          onChange={(e) => {
            setSort(e.target.value as ReportSort)
            setPage(0)
          }}
          sx={{ minWidth: { sm: 180 } }}
        >
          {SORTS.map((s) => (
            <MenuItem key={s.value} value={s.value}>
              {s.label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {/* keepPreviousData keeps the old page on screen while the next loads; this bar is what
          tells you the tap on "next page" registered. Fixed height so the list does not jump. */}
      <Box sx={{ height: 4, mb: 0.5 }}>{query.isFetching && !query.isLoading && <LinearProgress />}</Box>

      <DataTable<ReportedMediaFile>
        rows={query.data?.items ?? []}
        columns={columns}
        getRowKey={(file) => file.id}
        loading={query.isLoading}
        error={query.error ? apiErrorMessage(query.error, 'Failed to load files') : null}
        onRetry={() => query.refetch()}
        emptyMessage={search ? 'Nothing matches that search.' : emptyMessage}
        page={page}
        pageSize={pageSize}
        total={query.data?.totalCount ?? 0}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size)
          setPage(0)
        }}
        pagerPosition="both"
        getRowActions={(file) => [
          {
            key: 'open',
            label: 'Open file',
            icon: <OpenInNew fontSize="small" />,
            onClick: () => window.open(file.url, '_blank', 'noopener,noreferrer'),
          },
        ]}
        renderCard={(file) => (
          <Stack direction="row" spacing={1.25} sx={{ minWidth: 0 }}>
            <FileThumb file={file} size={48} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
                {file.displayName || file.fileName}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                component="div"
                sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}
              >
                {file.filePath}
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div">
                {formatBytes(file.fileSize)} · {file.category}
                {file.inFolder ? ' · in a folder' : ''} · {formatDateTime(file.createdDate)}
              </Typography>
              {view === 'missing' && file.isReferenced && (
                <Chip size="small" color="error" label="In use" sx={{ mt: 0.5 }} />
              )}
            </Box>
          </Stack>
        )}
      />
    </Box>
  )
}

/** Image preview, or the file type in a grey square for anything that is not an image (or is gone). */
function FileThumb({ file, size }: { file: ReportedMediaFile; size: number }) {
  return file.mimeType.startsWith('image/') && file.inBucket !== false ? (
    <Box
      component="img"
      src={file.url}
      alt=""
      loading="lazy"
      sx={{ width: size, height: size, flexShrink: 0, objectFit: 'cover', borderRadius: 1, bgcolor: 'action.hover', display: 'block' }}
    />
  ) : (
    <Box
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: 1,
        bgcolor: 'action.hover',
        display: 'grid',
        placeItems: 'center',
        fontSize: '0.65rem',
        color: 'text.secondary',
      }}
    >
      {file.mimeType.split('/')[1]?.slice(0, 4) || 'file'}
    </Box>
  )
}
